// src/components/accueil/personnaliser/ColonnesEditables.tsx — SCE Issues
// Mode « Personnaliser » de l'accueil (lot 3 du chantier du 03/11,
// docs/chantier-accueil.md, maquette https://claude.ai/artifact/W2ntCLABGPXsYQmGhaZeBZ) :
// les trois colonnes, chaque carte dans un cadre qu'on saisit par sa poignée
// pour la glisser ailleurs (trait jaune = là où elle tombera). Le menu ⋯ fait la
// même chose sans souris : monter, descendre, changer de colonne, retirer.
// En bas de chaque colonne, « Ajouter une carte » propose le catalogue, limité
// à ce que la personne a le droit de voir.
//
// Les cartes restent visibles dessous, atténuées et inertes : on range ce
// qu'on voit. Rien n'est enregistré avant « Terminé » (BarrePersonnaliser).
// Lot 6 : dans /admin › « Accueil par rôle », les mêmes cadres sans carte
// dessous, et le cadenas « Imposer à tous » dans le menu ⋯ (`onImposer`).

import { useRef, useState, type CSSProperties, type DragEvent, type JSX, type ReactNode } from 'react'
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, EyeOff, GripVertical, Lock, LockOpen, MoreHorizontal, Plus } from 'lucide-react'
import { Popover } from '@/components/ui/Popover'
import {
  ajouterCarte,
  CARTES_ACCUEIL,
  cartesAjoutables,
  decalerCarte,
  deplacerCarte,
  estIdCarte,
  masquerCarte,
  retirable,
  type ModeleAccueil,
} from '@/utils/dispositionAccueil'

export interface ColonnesEditablesProps {
  colonnes: string[][]
  modele: ModeleAccueil
  disponible: (id: string) => boolean
  /** Les files de la personne : chacune peut venir en carte depuis le catalogue. */
  files: readonly string[]
  /** Le titre d'une carte de file (`file:…`). */
  titre: (id: string) => string
  onChange: (colonnes: string[][]) => void
  /** La carte telle qu'elle s'affiche, rendue inerte dans son cadre. */
  rendre: (id: string) => JSX.Element | null
  /** Place de la carte dans sa colonne (grande, petite), comme hors du mode. */
  place: (id: string, seule: boolean) => CSSProperties
  /** Disposition de la grille (gabarit, hauteur), comme hors du mode. */
  style: CSSProperties
  /** Modèle de /admin : imposer une carte à tous, ou la libérer. */
  onImposer?: (id: string) => void
}

export function ColonnesEditables({ colonnes, modele, disponible, files, titre, onChange, rendre, place, style, onImposer }: ColonnesEditablesProps): JSX.Element {
  const titreDe = (id: string): string => (estIdCarte(id) ? CARTES_ACCUEIL[id].titre : titre(id))
  const [glisse, setGlisse] = useState<string | null>(null)
  const [cible, setCible] = useState<{ col: number; rang: number } | null>(null)
  const refsColonnes = useRef<(HTMLDivElement | null)[]>([])

  // Le rang d'arrivée, compté sans la carte qu'on glisse (convention de deplacerCarte).
  function rangSous(col: number, y: number): number {
    const el = refsColonnes.current[col]
    if (!el) return 0
    const cadres = [...el.querySelectorAll<HTMLElement>('[data-carte]')].filter((c) => c.dataset.carte !== glisse)
    const i = cadres.findIndex((c) => {
      const r = c.getBoundingClientRect()
      return y < r.top + r.height / 2
    })
    return i < 0 ? cadres.length : i
  }

  function surSurvol(e: DragEvent<HTMLDivElement>, col: number): void {
    if (!glisse) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const rang = rangSous(col, e.clientY)
    if (cible?.col !== col || cible.rang !== rang) setCible({ col, rang })
  }

  function surDepot(e: DragEvent<HTMLDivElement>, col: number): void {
    e.preventDefault()
    const id = glisse
    const rang = rangSous(col, e.clientY)
    finir()
    if (id) onChange(deplacerCarte(colonnes, id, col, rang))
  }

  function finir(): void {
    setGlisse(null)
    setCible(null)
  }

  const ajoutables = cartesAjoutables(colonnes, disponible, files)

  return (
    <div style={style}>
      {colonnes.map((colonne, col) => {
        const visibles = colonne.filter((id) => id !== glisse)
        const trait = (rang: number): ReactNode =>
          cible && cible.col === col && cible.rang === rang ? <div className="perso-trait" aria-hidden="true" /> : null
        return (
          <div
            key={col}
            ref={(el) => { refsColonnes.current[col] = el }}
            className="perso-colonne"
            onDragOver={(e) => surSurvol(e, col)}
            onDrop={(e) => surDepot(e, col)}
            aria-label={`Colonne ${col + 1}`}
          >
            {colonne.map((id) => {
              const rang = visibles.indexOf(id)
              return (
                <CadreCarte
                  key={id}
                  id={id}
                  titre={titreDe(id)}
                  avant={rang >= 0 ? trait(rang) : null}
                  style={place(id, colonne.length === 1)}
                  glisse={glisse === id}
                  imposee={!retirable(id, modele)}
                  raisonImposee={modele.imposees.includes(id) ? 'Imposée par le modèle' : 'Toujours là'}
                  peutMonter={colonne.indexOf(id) > 0}
                  peutDescendre={colonne.indexOf(id) < colonne.length - 1}
                  peutGauche={col > 0}
                  peutDroite={col < colonnes.length - 1}
                  onDebut={(e) => {
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', id)
                    setGlisse(id)
                  }}
                  onFin={finir}
                  onDecaler={(sens) => onChange(decalerCarte(colonnes, id, sens))}
                  onColonne={(sens) => onChange(deplacerCarte(colonnes, id, col + sens, colonnes[col + sens]?.length ?? 0))}
                  onMasquer={() => onChange(masquerCarte(colonnes, id, modele))}
                  onImposer={onImposer && (!estIdCarte(id) || CARTES_ACCUEIL[id].masquable) ? () => onImposer(id) : undefined}
                  imposeeParModele={modele.imposees.includes(id)}
                >
                  {rendre(id)}
                </CadreCarte>
              )
            })}
            {trait(visibles.length)}
            {colonne.length === 0 && <div className="perso-vide">Déposez une carte ici</div>}
            {ajoutables.length > 0 && <AjoutCarte ids={ajoutables} titreDe={titreDe} onAjouter={(id) => onChange(ajouterCarte(colonnes, id, modele, col))} />}
          </div>
        )
      })}
    </div>
  )
}

function CadreCarte({
  id,
  titre,
  avant,
  style,
  glisse,
  imposee,
  raisonImposee,
  peutMonter,
  peutDescendre,
  peutGauche,
  peutDroite,
  onDebut,
  onFin,
  onDecaler,
  onColonne,
  onMasquer,
  onImposer,
  imposeeParModele,
  children,
}: {
  id: string
  titre: string
  avant: ReactNode
  style: CSSProperties
  glisse: boolean
  imposee: boolean
  raisonImposee: string
  peutMonter: boolean
  peutDescendre: boolean
  peutGauche: boolean
  peutDroite: boolean
  onDebut: (e: DragEvent<HTMLDivElement>) => void
  onFin: () => void
  onDecaler: (sens: -1 | 1) => void
  onColonne: (sens: -1 | 1) => void
  onMasquer: () => void
  onImposer?: () => void
  imposeeParModele: boolean
  children: ReactNode
}): JSX.Element {
  const [menu, setMenu] = useState<HTMLButtonElement | null>(null)
  const actions: { cle: string; libelle: string; icone: JSX.Element; actif: boolean; faire: () => void }[] = [
    { cle: 'haut', libelle: 'Monter', icone: <ArrowUp size={16} />, actif: peutMonter, faire: () => onDecaler(-1) },
    { cle: 'bas', libelle: 'Descendre', icone: <ArrowDown size={16} />, actif: peutDescendre, faire: () => onDecaler(1) },
    { cle: 'gauche', libelle: 'Vers la colonne de gauche', icone: <ArrowLeft size={16} />, actif: peutGauche, faire: () => onColonne(-1) },
    { cle: 'droite', libelle: 'Vers la colonne de droite', icone: <ArrowRight size={16} />, actif: peutDroite, faire: () => onColonne(1) },
  ]
  return (
    <>
      {avant}
      <div data-carte={id} className="perso-cadre" data-glisse={glisse || undefined} style={style}>
        <div className="perso-poignee" draggable onDragStart={onDebut} onDragEnd={onFin} title="Glisser pour déplacer">
          <GripVertical size={18} aria-hidden="true" />
          <span className="perso-titre">{titre}</span>
          {imposee && (
            <span className="perso-cadenas" title={raisonImposee}>
              <Lock size={13} aria-hidden="true" />
              {raisonImposee}
            </span>
          )}
          <button
            type="button"
            className="perso-menu"
            aria-label={`Ranger « ${titre} »`}
            aria-expanded={!!menu}
            onClick={(e) => setMenu(menu ? null : e.currentTarget)}
          >
            <MoreHorizontal size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="perso-apercu" aria-hidden="true">{children}</div>
      </div>
      {menu && (
        <Popover ancre={menu} onClose={() => setMenu(null)} titre={`Ranger « ${titre} »`} largeur={260} alignerADroite>
          <ul role="menu" className="perso-liste-menu">
            {actions.map((a) => (
              <li key={a.cle} role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={!a.actif}
                  onClick={() => {
                    setMenu(null)
                    a.faire()
                  }}
                >
                  {a.icone}
                  {a.libelle}
                </button>
              </li>
            ))}
            {onImposer && (
              <li role="none" className="perso-separateur">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenu(null)
                    onImposer()
                  }}
                >
                  {imposeeParModele ? <LockOpen size={16} /> : <Lock size={16} />}
                  {imposeeParModele ? 'Ne plus imposer' : 'Imposer à tous (cadenas)'}
                </button>
              </li>
            )}
            <li role="none" className="perso-separateur">
              <button
                type="button"
                role="menuitem"
                disabled={imposee}
                title={imposee ? raisonImposee : undefined}
                onClick={() => {
                  setMenu(null)
                  onMasquer()
                }}
              >
                {imposee ? <Lock size={16} /> : <EyeOff size={16} />}
                {imposee ? `${raisonImposee} : ne se retire pas` : 'Retirer de l’accueil'}
              </button>
            </li>
          </ul>
        </Popover>
      )}
    </>
  )
}

function AjoutCarte({ ids, titreDe, onAjouter }: { ids: readonly string[]; titreDe: (id: string) => string; onAjouter: (id: string) => void }): JSX.Element {
  const [ancre, setAncre] = useState<HTMLButtonElement | null>(null)
  return (
    <>
      <button type="button" className="perso-ajout" aria-expanded={!!ancre} onClick={(e) => setAncre(ancre ? null : e.currentTarget)}>
        <Plus size={16} aria-hidden="true" />
        Ajouter une carte
      </button>
      {ancre && (
        <Popover ancre={ancre} onClose={() => setAncre(null)} titre="Ajouter une carte" largeur={300}>
          <div className="popover-entete">
            <span>Ajouter une carte</span>
          </div>
          <ul role="menu" className="perso-liste-menu">
            {ids.map((id) => (
              <li key={id} role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="perso-catalogue"
                  onClick={() => {
                    setAncre(null)
                    onAjouter(id)
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{titreDe(id)}</span>
                  <span className="perso-catalogue-detail">{estIdCarte(id) ? CARTES_ACCUEIL[id].description : 'Cette file en permanence, à côté de la file choisie.'}</span>
                </button>
              </li>
            ))}
          </ul>
        </Popover>
      )}
    </>
  )
}
